import { z } from 'zod';
import {
  EqualisationSection,
  FeesEqualisationTreatment,
} from 'further-types/lp/equalisation';
import {
  LpFeeCalculationBasis,
  LpFeeCommitmentStatus,
} from 'further-types/lp/lp-fee-template';
import { isMongoId } from '../../helpers';
import { CalendarDay } from '../../../utils';
import { AllocationRule } from 'further-types/lp/allocations';
import { AllocationSchema, LinkedActionSchema } from '../reallocation-layer';

const LpFeeSchema = z.object({
  id: z.string().refine(isMongoId()),
  lpFeeTemplateFeeId: z.string().refine(isMongoId()),
  accrualDate: z.instanceof(CalendarDay),
  feeTemplateName: z.string(),
  insideOutsideCommitment: z.nativeEnum(LpFeeCommitmentStatus),
  calculationBasis: z.nativeEnum(LpFeeCalculationBasis),
  equalisationFeeExTaxLcy: z.number(),
  equalisationSalesTaxAmountLcy: z.number(),
  equalisationFeeIncTaxLcy: z.number(),
  salesTaxPercentage: z.number(),
  feeExTaxLcy: z.number(),
  totalCommitmentLcy: z.number().optional(),
});

const EqualisationActionSchema = z.object({
  actionId: z.string().refine(isMongoId()),
  actionType: z.string(),
  counterpartyLabel: z.string(),
  ledgerDate: z.instanceof(CalendarDay),
  totalAmountLcy: z.number(),
  allocationRule: z.nativeEnum(AllocationRule),
  originalAllocations: z.array(AllocationSchema).optional(),
  newAllocations: z.array(AllocationSchema),
  linkedActions: z.array(LinkedActionSchema),
});

const EqualisationsTotalCommitmentFeesSchema = z.object({
  commitmentId: z.string().refine(isMongoId()),
  lpFeeTemplateId: z.string().refine(isMongoId()).optional(),
  feesEqualisationTreatment: z.nativeEnum(FeesEqualisationTreatment).optional(),
  investorName: z.string().optional(),
  investorStatus: z.string().optional(),
  feesEqualisationTreatmentPercentage: z.number().optional(),
  totalEqualisationFeeIncTaxLcy: z.number().optional(),
  lpFees: z.array(LpFeeSchema),
  lpFeesByTemplate: z.record(
    z.string(),
    z.object({
      lpFees: z.array(LpFeeSchema),
      feesEqualisationTreatment: z.nativeEnum(FeesEqualisationTreatment),
      feesEqualisationTreatmentPercentage: z.number(),
    }),
  ),
});

export const EqualisationsTotalCommitmentFeesFormSchema = z.object({
  totalCommitmentFees: z.array(EqualisationsTotalCommitmentFeesSchema),
});

const InvestorBreakdownSchema = z.object({
  commitmentId: z.string().refine(isMongoId()),
  investorName: z.string(),
  totalCommitmentLcy: z.number(),
  calledFundsLcy: z.number(),
  allocationPercentage: z.number(),
  excludeFromAllocation: z.boolean(),
});

const NewInvestorBreakdownSchema = z.object({
  commitmentId: z.string().refine(isMongoId()),
  investorName: z.string(),
  totalCommitmentLcy: z.number(),
  calledFundsLcy: z.number(),
  allocationPercentage: z.number(),
  callOverpaidOrUnderpaid: z.number(),
  excludeFromAllocation: z.boolean(),
});

export const GeneralCapitalCallFormSchema = z.object({
  actionId: z.string().refine(isMongoId()),
  entryId: z.string().refine(isMongoId()),
  name: z.string(),
  ledgerDate: z.instanceof(CalendarDay),
  actionDueDate: z.instanceof(CalendarDay).optional(),
  totalAmountLcy: z.number(),
  allocationRule: z.nativeEnum(AllocationRule),
  originalInvestorBreakdown: z.array(InvestorBreakdownSchema),
  newInvestorBreakdown: z.array(NewInvestorBreakdownSchema),
  numberOfEntries: z.number(),
});

export const UpdateEqualisationGeneralCapitalCallsFormSchema = z.object({
  generalCapitalCalls: z.array(GeneralCapitalCallFormSchema),
});

const EqualisationInterestPaymentBreakdownSchema = z.object({
  commitmentId: z.string().refine(isMongoId()),
  investorName: z.string(),
  equalisationInterestTotalLcy: z.number(),
  equalisationInterestPercentage: z.number(),
  aggregateTotalLcy: z.number(),
  excludeFromEqualisationInterest: z.boolean().optional(),
});

export const UpdateEqualisationInterestFormSchema = z.object({
  equalisationInterestPaymentBreakdown: z.array(
    EqualisationInterestPaymentBreakdownSchema,
  ),
});

export const CommitmentFormSchema = z.object({
  id: z.string().refine(isMongoId()),
  investorName: z.string(),
  isCarryPartnerCommitment: z.boolean().optional(),
  totalCommitmentLcy: z.number(),
  loanCommitmentLcy: z.number(),
  capitalCommitmentLcy: z.number(),
  existingCapitalCommitmentLcy: z.number().optional(),
  existingLoanCommitmentLcy: z.number().optional(),
  existingTotalCommitmentLcy: z.number().optional(),
  isCapitalContributionRuleOverride: z.boolean().optional(),
  isSelected: z.boolean(),
  equalisationDate: z.instanceof(CalendarDay).optional().nullable(),
});

export const UpdateEqualisationSchema = z.object({
  equalisationId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
  currentSection: z.nativeEnum(EqualisationSection),
  actions: z.array(EqualisationActionSchema).optional(),
  totalCommitmentFees: z
    .array(EqualisationsTotalCommitmentFeesSchema)
    .optional(),
  generalCapitalCalls: z.array(GeneralCapitalCallFormSchema).optional(),
  equalisationInterestPaymentBreakdown: z
    .array(EqualisationInterestPaymentBreakdownSchema)
    .optional(),
  saveActionLinkedCapitalActions: z.boolean().optional(),
  saveCorrectiveEntries: z.boolean().optional(),
  commitments: z.array(CommitmentFormSchema).optional(),
  closeDate: z.instanceof(CalendarDay).optional(),
  name: z.string().optional(),
});

export const UpdateEqualisationActionsFormSchema = z.object({
  currentSection: z.nativeEnum(EqualisationSection),
  actions: z.array(EqualisationActionSchema),
});

export const getAddCommitmentsToEqualisationFormSchema = (
  isEditingApprovedClose = false,
) =>
  z.object({
    closeDate: z.instanceof(CalendarDay, { message: 'Close date is required' }),
    name: z.string().min(1, 'Close name is required'),
    commitments: z
      .array(CommitmentFormSchema)
      .refine(
        (commitments) =>
          commitments.some(
            (c) =>
              c.isSelected && (!c.equalisationDate || isEditingApprovedClose),
          ),
        {
          message: 'Please select at least one commitment',
        },
      )
      .superRefine((commitments, ctx) => {
        if (isEditingApprovedClose) return;
        commitments.forEach((c, index) => {
          const hasEqualisationDateAndNotCarryPartner =
            c.equalisationDate != null && !c.isCarryPartnerCommitment;
          if (!hasEqualisationDateAndNotCarryPartner) return;
          if (c.totalCommitmentLcy !== c.existingTotalCommitmentLcy) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                'This new total commitment does not match the existing total. Please update the capital contribution or loan commitment so the new total matches the existing total.',
              path: [index, 'totalCommitmentLcy'],
            });
          }
        });
      }),
  });

export const AddCommitmentsToEqualisationFormSchema =
  getAddCommitmentsToEqualisationFormSchema();
